import { groupFilesbyParentId } from '@/_utils/titleUtils';
import { useToast } from '@/components/_common/Toast';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectInventorySelections, selectTitleInput, selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { addOutputVersion, removeInventorySelection, setTitleInput } from '@/redux/slices/orderPlacementSlice';
import { Box, Button, Fade, Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { memo, useEffect, useRef, useState } from 'react';
import SchemaForm from '../../../_common/SchemaForm/SchemaForm';
import usePrevious from '../../../_hooks/usePreviousValue';
import useProfile from '../../../_hooks/useProfile';
import InventoryFile from '@/components/_common/InventoryFile/InventoryFile';
import PrimaryStarButton from '../PrimaryStarButton';
import OPFileSelectionTitle from './OPFileSelectionTitle';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

// *******
// Component that is rendered when the user has selected files to be added to the order.
// It is rendered in the OPStep2FileSelection component, and has a dashed border.
// *******

const OPFileSelectionsPending = ({ primaryVersion, setPrimaryVersion }) => {
  const dispatch = useAppDispatch();
  const selectedInventory = useAppSelector(selectInventorySelections);
  const groupedByParentId = groupFilesbyParentId(selectedInventory);
  const titleInput = useAppSelector(selectTitleInput);
  const { values } = useFormikContext();
  const { titleSchema, outputVersionKey } = useProfile(values);
  const versionKeys = useAppSelector(selectVersionKeys);
  const { error } = useToast();
  const [formValues, setFormValues] = useState(titleInput || values.jobs[0].title_inputs || {});
  const titleSchemaFormRef = useRef();
  const prevPrimaryVersion = usePrevious(primaryVersion);

  const onChangeFormValues = (values) => {
    dispatch(setTitleInput(values));
    setFormValues(values);
  };

  useEffect(() => {
    if (prevPrimaryVersion && prevPrimaryVersion !== primaryVersion) {
      onChangeFormValues({ ...values.jobs?.[0]?.title_inputs, [outputVersionKey]: undefined });
    }
  }, [primaryVersion]);

  useEffect(() => {
    if (selectedInventory.length > 0) {
      if (!primaryVersion) {
        setPrimaryVersion(Object.keys(groupedByParentId)?.[0]);
      }
      // if the previously selected primary version no longer exists,
      // clear it if we are only left with 1 version to choose from,
      // otherwise select the first one
      const primaryVersionExists = selectedInventory.find(({ version_external_id }) => primaryVersion === version_external_id);
      if (!primaryVersionExists) {
        if (Object.keys(groupedByParentId).length > 0) {
          setPrimaryVersion(Object.keys(groupedByParentId)?.[0]);
        } else {
          setPrimaryVersion(null);
        }
      }
    }
  }, [selectedInventory]);

  const onRemoveFile = (file) => {
    dispatch(removeInventorySelection(file));
  };

  const addJobToOrder = () => {
    const hasErrors = titleSchemaFormRef.current && !titleSchemaFormRef.current?.validateForm();
    if (hasErrors) {
      return error('Some fields have errors.');
    } else {
      dispatch(addOutputVersion({ primary_version: primaryVersion, title_inputs: formValues }));
    }
  };

  return (
    <div>
      {/* displays only when we have files from multiple versions */}
      {Object.keys(groupedByParentId).map((parentId) => {
        return (
          <Box key={parentId} sx={{ mb: 4 }}>
            {Object.keys(groupedByParentId).length > 1 && (
              <Grid container flexWrap="nowrap" alignItems="center">
                <PrimaryStarButton selected={parentId == primaryVersion} onClick={() => setPrimaryVersion(parentId)} />
                <OPFileSelectionTitle version={versionKeys[parentId]} id={parentId} />
              </Grid>
            )}
            {groupedByParentId[parentId].map((file, i) => (
              // there are duplicate file IDs currently, so appending index to key
              <Fade in key={`file-${file.id}-${i}`} timeout={(i + 1) * 400}>
                <div>
                  <InventoryFile file={file} onRemove={() => onRemoveFile(file)} disableSelected xOnly showTitleLabel />
                </div>
              </Fade>
            ))}
          </Box>
        );
      })}

      {titleSchema?.properties && !!Object.keys(titleSchema?.properties).length && (
        <SchemaForm
          key="title_schema_form"
          tagName="div"
          formRef={titleSchemaFormRef}
          schema={titleSchema}
          formContext={{ programId: versionKeys[primaryVersion]?.program_id }}
          formData={formValues}
          onChange={(data) => onChangeFormValues(data.formData)}
          alternateColor
        />
      )}
      <Grid container alignItems="center">
        <Button color="primary" variant="outlined" sx={{ mt: 2 }} startIcon={<PolarisIcon icon="up_double" />} onClick={addJobToOrder}>
          Add to Order
        </Button>
      </Grid>
    </div>
  );
};

export default memo(OPFileSelectionsPending);
